/**
 * ModalImportarIncidenciasExcel.js
 *
 * Sube el Excel de incidencias del periodo (matriz empleados × días), lo previsualiza
 * y lo manda a prenomina/importarIncidenciasExcel.
 *
 * Es un modal y no una pantalla con ImportadorExcelGenerico a propósito: ése navega
 * con `history` y aquí no se puede perder la prenómina abierta, que es la que define
 * el periodo, los empleados y los días contra los que se valida el archivo.
 *
 * El parseo y la plantilla viven en plantillaIncidencias.js; aquí sólo se orquesta.
 */

import React, { Component } from 'react'
import { Alert, Modal, Upload, Row, Col, Tag, Tooltip, Select, Table, Dropdown, Menu, message } from 'antd'
import { UploadOutlined, WarningOutlined, DownloadOutlined, DownOutlined, ApartmentOutlined } from '@ant-design/icons'
import moment from 'moment'
import * as XLSX from 'xlsx-js-style'

import { ButtonItem, SelectItem } from '../../../../Components/Items'
import DatePickerItem from '../../../../Components/DatePickerItem'
import {
  construirPlantillaIncidencias,
  nombreArchivoPlantilla,
  analizarHojaIncidencias,
  construirFilasPayload,
  resolverTipoVacaciones,
  tiposActivos,
} from './plantillaIncidencias'

const { Option } = Select

const HOJA_PREFERIDA = 'Incidencias'

class ModalImportarIncidenciasExcel extends Component {
  state = {
    archivos: [],
    analisis: null,
    error: null,
    procesando: false,
    importando: false,
    tipoVacaciones: 'vacaciones_y_prima',
    fechaPago: null,
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) this.reset()
  }

  reset() {
    this.setState({
      archivos: [], analisis: null, error: null, procesando: false, importando: false,
      tipoVacaciones: 'vacaciones_y_prima',
      fechaPago: this.props.prenomina?.fechaFin ? moment(this.props.prenomina.fechaFin) : null,
    })
  }

  // ── Plantilla ──────────────────────────────────────────────────────────────

  /**
   * `porDepartamento` sólo cambia el orden de los renglones y agrega los banners de área; el
   * archivo se importa igual en los dos casos, así que no hay que recordar cuál se descargó.
   */
  descargarPlantilla = (porDepartamento = false) => {
    const { prenomina, tiposIncidencias } = this.props
    try {
      const libro = construirPlantillaIncidencias({ prenomina, tiposIncidencias, porDepartamento })
      XLSX.writeFile(libro, nombreArchivoPlantilla(prenomina, { porDepartamento }))
    } catch (err) {
      console.log(err)
      message.error('No se pudo generar la plantilla')
    }
  }

  // ── Archivo ────────────────────────────────────────────────────────────────

  onSubirArchivo = ({ file, onSuccess }) => {
    this.setState({ procesando: true, analisis: null, error: null })
    const lector = new FileReader()
    lector.readAsArrayBuffer(file)
    lector.onloadend = (e) => {
      try {
        const libro = XLSX.read(new Uint8Array(e.target.result), { type: 'array' })
        const nombreHoja = libro.SheetNames.includes(HOJA_PREFERIDA)
          ? HOJA_PREFERIDA
          : libro.SheetNames[0]
        const filas = XLSX.utils.sheet_to_json(libro.Sheets[nombreHoja], {
          header: 1, defval: '', blankrows: true, raw: false,
        })

        const analisis = analizarHojaIncidencias({
          filas,
          prenomina: this.props.prenomina,
          tiposIncidencias: this.props.tiposIncidencias,
        })

        if (analisis.error) message.error(analisis.error)
        this.setState({
          procesando: false,
          analisis: analisis.error ? null : analisis,
          error: analisis.error,
          archivos: [{
            uid: '1', name: file.name, status: analisis.error ? 'error' : 'done',
          }],
        })
        if (onSuccess) onSuccess('ok')
      } catch (err) {
        console.log(err)
        message.error('No se pudo leer el archivo. Verifica que sea un .xlsx válido.')
        this.setState({ procesando: false, archivos: [] })
      }
    }
  }

  // ── Importar ───────────────────────────────────────────────────────────────

  importar = () => {
    const { analisis, tipoVacaciones, fechaPago } = this.state
    const { prenomina } = this.props
    if (!analisis) return

    const filasPayload = construirFilasPayload(analisis.filas)
    if (filasPayload.length === 0) {
      message.error('No hay ningún renglón que se pueda importar')
      return
    }

    const { resumen } = analisis
    const avisos = []
    if (resumen.conError) avisos.push(`${resumen.conError} renglón(es) con error — se van a omitir.`)
    if (resumen.bloqueadas) avisos.push(`${resumen.bloqueadas} empleado(s) con el recibo ya timbrado — se van a omitir.`)
    if (resumen.diasReemplazados) avisos.push(`${resumen.diasReemplazados} día(s) ya tenían una incidencia capturada y se van a reemplazar.`)

    const ejecutar = async () => {
      this.setState({ importando: true })
      const ok = await this.props.onImportar({
        filas: filasPayload,
        vacaciones: {
          tipo: tipoVacaciones,
          fechaPago: (fechaPago || moment(prenomina?.fechaFin)).format('YYYY-MM-DD'),
        },
      })
      this.setState({ importando: false })
      if (ok) this.props.onCerrar()
    }

    if (avisos.length) {
      Modal.confirm({
        title: '¿Importar de todos modos?',
        width: 560,
        content: (
          <div>
            {avisos.map((a, i) => <p key={i} style={{ marginBottom: 6 }}>{a}</p>)}
            <p style={{ marginTop: 12, marginBottom: 0 }}>
              Se van a cargar <b>{filasPayload.length}</b> empleado(s)
              {resumen.movimientosVacaciones > 0 && (
                <> y a crear <b>{resumen.movimientosVacaciones}</b> movimiento(s) de vacaciones</>
              )}.
            </p>
          </div>
        ),
        okText: 'Importar',
        cancelText: 'Revisar',
        onOk: ejecutar,
      })
    } else {
      ejecutar()
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  columnas() {
    return [
      { title: 'Numero', dataIndex: 'numeroArchivo', key: 'numeroArchivo', width: 90 },
      {
        title: 'Empleado', key: 'empleado', width: 220,
        render: (_, r) => r.empleadoNombre || (
          <span style={{ color: '#aaa', fontStyle: 'italic' }}>{r.nombreArchivo || '—'}</span>
        ),
      },
      {
        title: 'Incidencias', key: 'incidencias', width: 200,
        render: (_, r) => r.resumenIncidencias.length === 0
          ? <span style={{ color: '#ccc' }}>—</span>
          : r.resumenIncidencias.map((i) => (
              <Tag key={i.clave} color="blue" style={{ fontSize: 10, marginBottom: 2 }}>
                {i.clave} ×{i.total}
              </Tag>
            )),
      },
      {
        title: 'Vacaciones', key: 'vacaciones', width: 210,
        render: (_, r) => r.rangosVacaciones.length === 0
          ? <span style={{ color: '#ccc' }}>—</span>
          : r.rangosVacaciones.map((v, i) => (
              <div key={i} style={{ fontSize: 11, color: '#531DAB' }}>
                {v.inicio?.format('DD/MM')} → {v.fin?.format('DD/MM')} · {v.dias} día(s)
                {v.diasHabiles !== v.dias && (
                  <Tooltip title={`Se pagan los ${v.dias} días marcados; el kárdex descuenta ${v.diasHabiles} (el domingo no cuenta).`}>
                    <span style={{ color: '#aaa' }}> · {v.diasHabiles} al saldo</span>
                  </Tooltip>
                )}
              </div>
            )),
      },
      {
        title: 'Estado', key: 'estado', width: 230,
        render: (_, r) => {
          if (r.errores?.length) {
            return (
              <Tooltip title={r.errores.join(' · ')}>
                <span style={{ color: '#cf1322' }}><WarningOutlined /> {r.errores[0]}</span>
              </Tooltip>
            )
          }
          if (r.bloqueada) return <Tag color="default">Recibo timbrado, se omite</Tag>
          if (r.vacio) return <span style={{ color: '#bbb' }}>Sin captura</span>
          if (r.reemplaza > 0) {
            return <Tag color="orange">Reemplaza {r.reemplaza} día(s)</Tag>
          }
          return <Tag color="green">Nuevo</Tag>
        },
      },
    ]
  }

  render() {
    const { visible, onCerrar, prenomina, tiposIncidencias } = this.props
    const { archivos, analisis, procesando, importando, tipoVacaciones, fechaPago } = this.state

    const resumen = analisis?.resumen
    const hayVacaciones = !!resumen && resumen.movimientosVacaciones > 0
    const tipoVac = resolverTipoVacaciones(tiposActivos(tiposIncidencias || []))
    const puedeImportar = !!resumen && resumen.importables > 0 && !importando && !procesando

    return (
      <Modal
        title="Importar incidencias desde Excel"
        visible={visible}
        onCancel={onCerrar}
        width={1000}
        footer={[
          <ButtonItem key="cancelar" onClick={onCerrar}>Cancelar</ButtonItem>,
          <ButtonItem
            key="importar"
            type="primary"
            disabled={!puedeImportar}
            loading={importando}
            onClick={this.importar}
          >
            Importar
          </ButtonItem>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Captura de la semana en un solo archivo"
          description={
            <span>
              Descarga la plantilla de este periodo —ya trae a los empleados de la nómina y una
              columna por día—, escribe la clave de la incidencia en cada día y súbela. Los días que
              marques como <b>{tipoVac?.clave || 'VAC'}</b> no se guardan como incidencia: se
              registran en el <b>módulo de vacaciones</b> como un movimiento por cada racha de días
              seguidos, y el sistema los devuelve a la rejilla al calcular. Al terminar hay que
              presionar <b>Calcular todo</b>.
            </span>
          }
        />

        <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>1. Plantilla del periodo</div>
            <Dropdown
              trigger={['click']}
              disabled={!prenomina?.lineas?.length}
              overlay={(
                <Menu>
                  <Menu.Item key="completa" icon={<DownloadOutlined />}
                    onClick={() => this.descargarPlantilla(false)}>
                    Plantilla completa
                  </Menu.Item>
                  {/* Mismo archivo, sólo que los empleados salen agrupados por área con un
                      renglón separador: sirve para repartir la captura entre departamentos sin
                      partir la nómina en varios archivos. */}
                  <Menu.Item key="departamento" icon={<ApartmentOutlined />}
                    onClick={() => this.descargarPlantilla(true)}>
                    Agrupada por departamento
                  </Menu.Item>
                </Menu>
              )}
            >
              <ButtonItem
                celeste
                icon={<DownloadOutlined />}
                disabled={!prenomina?.lineas?.length}
              >
                Descargar plantilla <DownOutlined />
              </ButtonItem>
            </Dropdown>
          </Col>
          <Col xs={24} sm={14}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>2. Archivo capturado</div>
            <Upload
              showUploadList={{ showRemoveIcon: false, showPreviewIcon: false }}
              fileList={archivos}
              customRequest={this.onSubirArchivo}
              accept=".xlsx"
            >
              <ButtonItem icon={<UploadOutlined />} loading={procesando}>Subir Excel</ButtonItem>
            </Upload>
          </Col>
        </Row>

        {hayVacaciones && (
          <div style={{
            border: '1px solid #d3adf7', background: '#f9f0ff',
            borderRadius: 6, padding: 12, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, color: '#531DAB', fontWeight: 600, marginBottom: 8 }}>
              El archivo trae vacaciones — {resumen.movimientosVacaciones} movimiento(s) en{' '}
              {resumen.conVacaciones} empleado(s)
            </div>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Tipo de captura</div>
                <SelectItem
                  style={{ width: '100%' }}
                  value={tipoVacaciones}
                  noFocusSiguiente
                  onChange={(v) => this.setState({ tipoVacaciones: v })}
                >
                  <Option value="vacaciones_y_prima">Vacaciones y prima vacacional</Option>
                  <Option value="solo_vacaciones">Solo vacaciones</Option>
                </SelectItem>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Fecha de pago</div>
                <DatePickerItem
                  style={{ width: '100%' }}
                  value={fechaPago}
                  onChange={(d) => this.setState({ fechaPago: d })}
                />
              </Col>
            </Row>
            <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
              Se aplican a todos los movimientos de vacaciones del archivo.
            </div>
          </div>
        )}

        {resumen && (
          <Alert
            type={resumen.conError > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <>
                <b>{resumen.importables}</b> empleado(s) a importar
                {resumen.diasReemplazados > 0 && <> · <b>{resumen.diasReemplazados}</b> día(s) se reemplazan</>}
                {resumen.conError > 0 && <> · <b>{resumen.conError}</b> con error</>}
                {resumen.bloqueadas > 0 && <> · <b>{resumen.bloqueadas}</b> timbrados</>}
                {resumen.sinCaptura > 0 && <> · <b>{resumen.sinCaptura}</b> sin captura</>}
              </>
            }
            description={
              [
                resumen.diasReemplazados > 0
                  ? 'En los renglones que sí traen captura, una celda vacía sobre un día que ya tenía incidencia también la borra: el archivo es el que manda.'
                  : null,
                resumen.sinCaptura > 0
                  ? 'Los renglones que vienen completamente vacíos se toman como "no lo toqué" y no se modifican.'
                  : null,
              ].filter(Boolean).join(' ') || undefined
            }
          />
        )}

        {/* Table de antd y no TableItem: TableItem fuerza scroll, pagination y ellipsis en
            todas las columnas, y aquí hacen falta las etiquetas de incidencias envueltas y
            los rangos de vacaciones en varios renglones. */}
        {analisis && (
          <Table
            size="small"
            rowKey={(r) => `${r.filaExcel}`}
            dataSource={analisis.filas}
            columns={this.columnas()}
            pagination={analisis.filas.length > 50 ? { pageSize: 50, size: 'small' } : false}
            scroll={{ y: 320 }}
          />
        )}
      </Modal>
    )
  }
}

export default ModalImportarIncidenciasExcel
